import { apiClient } from "./client";
import type {
  CandidateType,
  Layer,
  LayerInfo,
  PresignedUrlResponse,
  Project,
  ProjectCreateResponse,
  ProjectDetail,
  ProjectEventsResponse,
  ProjectsListResponse,
  ReclassifyResponseBody,
  Run,
  RunLayout,
  SitePlanCandidate,
  SitePlanCandidateAddResponse,
  Study,
  WorkflowType,
} from "../types/projects";

/* ----- Projects ----- */

export async function listProjects(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ProjectsListResponse> {
  const r = await apiClient.get<ProjectsListResponse>("/api/projects", {
    params,
  });
  return r.data;
}

export async function createProject(payload: {
  name: string;
  description?: string;
}): Promise<ProjectCreateResponse> {
  const r = await apiClient.post<ProjectCreateResponse>(
    "/api/projects",
    payload,
  );
  return r.data;
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const r = await apiClient.get<ProjectDetail>(`/api/projects/${id}`);
  return r.data;
}

export async function patchProject(
  id: string,
  payload: { name?: string; description?: string; status?: string },
): Promise<Project> {
  const r = await apiClient.patch<Project>(`/api/projects/${id}`, payload);
  return r.data;
}

export async function archiveProject(id: string): Promise<void> {
  await apiClient.delete(`/api/projects/${id}`, { params: { soft: "true" } });
}

/**
 * Hard delete: removes the project, all studies/runs/layers, and every
 * S3 object under the project's prefix. The backend route's default is
 * a full cascade; no ``?soft=true`` here.
 */
export async function deleteProject(id: string): Promise<void> {
  await apiClient.delete(`/api/projects/${id}`);
}

/* ----- Studies ----- */

export async function listStudies(projectId: string): Promise<Study[]> {
  const r = await apiClient.get<Study[]>(
    `/api/projects/${projectId}/studies`,
  );
  return r.data;
}

export async function createStudy(
  projectId: string,
  payload: { name: string; workflow_type?: WorkflowType },
): Promise<Study> {
  const r = await apiClient.post<Study>(
    `/api/projects/${projectId}/studies`,
    { workflow_type: "ac_block_layout" as WorkflowType, ...payload },
  );
  return r.data;
}

export async function deleteStudy(studyId: string): Promise<void> {
  await apiClient.delete(`/api/studies/${studyId}`);
}

/* ----- Runs ----- */

export async function listRuns(studyId: string): Promise<Run[]> {
  const r = await apiClient.get<Run[]>(`/api/studies/${studyId}/runs`);
  return r.data;
}

export async function createRun(
  studyId: string,
  inputSnapshot: Record<string, unknown> = {},
): Promise<Run> {
  const r = await apiClient.post<Run>(`/api/studies/${studyId}/runs`, {
    input_snapshot: inputSnapshot,
  });
  return r.data;
}

export async function getRun(runId: string): Promise<Run> {
  const r = await apiClient.get<Run>(`/api/runs/${runId}`);
  return r.data;
}

export async function pinRun(runId: string): Promise<Run> {
  const r = await apiClient.post<Run>(`/api/runs/${runId}/pin`);
  return r.data;
}

export async function deleteRun(runId: string): Promise<void> {
  await apiClient.delete(`/api/runs/${runId}`);
}

/**
 * Stage L.4.d: fetch the persisted layout payload for a run. Mirrors
 * GET /api/runs/{run_id}/layout.json — returns ``runs.output_data``
 * verbatim. ``useEditOps`` mutates this cache via ``setQueryData``
 * after every add/move/delete.
 */
export async function getRunLayout(runId: string): Promise<RunLayout> {
  const r = await apiClient.get<RunLayout>(`/api/runs/${runId}/layout.json`);
  return r.data;
}

/* ----- Files / Layers ----- */

export async function listLayers(projectId: string): Promise<Layer[]> {
  const r = await apiClient.get<Layer[]>(`/api/projects/${projectId}/files`);
  return r.data;
}

export async function getBoundary(projectId: string): Promise<Layer | null> {
  try {
    const r = await apiClient.get<Layer>(`/api/projects/${projectId}/boundary`);
    return r.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export async function getPresignedUrl(
  projectId: string,
  filename: string,
  expectedLayerType: "boundary" | "generic" = "generic",
): Promise<PresignedUrlResponse> {
  const r = await apiClient.post<PresignedUrlResponse>(
    `/api/projects/${projectId}/files/presigned-url`,
    { filename, expected_layer_type: expectedLayerType },
  );
  return r.data;
}

export async function confirmUpload(
  projectId: string,
  layerId: string,
  expectedLayerType: "boundary" | "generic" = "generic",
): Promise<Layer> {
  const r = await apiClient.post<Layer>(
    `/api/projects/${projectId}/files/confirm`,
    { layer_id: layerId, expected_layer_type: expectedLayerType },
  );
  return r.data;
}

export async function deleteLayer(
  projectId: string,
  layerId: string,
): Promise<void> {
  await apiClient.delete(`/api/projects/${projectId}/files/${layerId}`);
}

export async function reclassifyLayer(
  projectId: string,
  layerId: string,
  hint?: string | null,
): Promise<ReclassifyResponseBody> {
  const r = await apiClient.post<ReclassifyResponseBody>(
    `/api/projects/${projectId}/files/${layerId}/reclassify`,
    { hint: hint ?? null },
  );
  return r.data;
}

/** Manually override the agent's per-feature classification. Returns the
 * refreshed Layer with the updated ``feature_classifications``. */
export async function setFeatureClassification(
  projectId: string,
  layerId: string,
  featureId: string,
  suggestedType: CandidateType,
): Promise<Layer> {
  const r = await apiClient.patch<Layer>(
    `/api/projects/${projectId}/files/${layerId}/features/${encodeURIComponent(
      featureId,
    )}/classification`,
    { suggested_type: suggestedType },
  );
  return r.data;
}

/**
 * URL string for the per-file preview thumbnail endpoint.
 *
 * NOTE: ``<img src={url}>`` cannot send a Bearer token, so the
 * endpoint will 401 against this raw URL in production. Use
 * :func:`fetchLayerPreviewThumbnail` to get a blob URL that goes
 * through the authed ``apiClient``. This factory remains exported only
 * so tests can mock the endpoint shape.
 */
export function layerPreviewThumbnailUrl(
  projectId: string,
  layerId: string,
): string {
  return `/api/projects/${projectId}/files/${layerId}/preview-thumbnail`;
}

/**
 * Fetch the per-file preview thumbnail via the authed apiClient and
 * return the response as a blob. Throws on non-2xx so React Query can
 * surface the error (e.g. 404 for unsupported file types → caller
 * shows "No preview available").
 */
export async function fetchLayerPreviewThumbnail(
  projectId: string,
  layerId: string,
): Promise<Blob> {
  const r = await apiClient.get<Blob>(
    `/api/projects/${projectId}/files/${layerId}/preview-thumbnail`,
    { responseType: "blob" },
  );
  return r.data;
}

/** URL to the boundary thumbnail endpoint (SVG). */
export function boundaryThumbnailUrl(projectId: string): string {
  return `/api/projects/${projectId}/boundary-thumbnail.svg`;
}

/** Stage 8.6: lazy file metadata for the preview panel. */
export async function getLayerInfo(
  projectId: string,
  layerId: string,
): Promise<LayerInfo> {
  const r = await apiClient.get<LayerInfo>(
    `/api/projects/${projectId}/files/${layerId}/info`,
  );
  return r.data;
}

/* ----- Stage 8.7: site-plan candidates pool ----- */

/**
 * Route features from an uploaded layer into the project's site-plan
 * pool. Omit ``feature_id`` to bulk-add every feature in the layer
 * with its auto-classification. Pass a specific ``feature_id`` (and
 * optionally a type override) to add just one feature.
 *
 * Idempotent: re-adding the same layer skips the rows that already
 * exist (returned in ``skipped`` rather than as duplicate rows).
 */
export async function addToSitePlanCandidates(
  projectId: string,
  payload: {
    layer_id: string;
    feature_id?: string;
    candidate_type_override?: CandidateType;
  },
): Promise<SitePlanCandidateAddResponse> {
  const r = await apiClient.post<SitePlanCandidateAddResponse>(
    `/api/projects/${projectId}/site-plan-candidates`,
    payload,
  );
  return r.data;
}

export async function listSitePlanCandidates(
  projectId: string,
  candidateType?: CandidateType,
): Promise<SitePlanCandidate[]> {
  const r = await apiClient.get<SitePlanCandidate[]>(
    `/api/projects/${projectId}/site-plan-candidates`,
    { params: candidateType ? { type: candidateType } : undefined },
  );
  return r.data;
}

export async function removeSitePlanCandidate(
  projectId: string,
  candidateId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/projects/${projectId}/site-plan-candidates/${candidateId}`,
  );
}

/* ----- Stage 8.7: project events (activity log) ----- */

export async function listProjectEvents(
  projectId: string,
  params?: { limit?: number; before?: string },
): Promise<ProjectEventsResponse> {
  const r = await apiClient.get<ProjectEventsResponse>(
    `/api/projects/${projectId}/events`,
    { params },
  );
  return r.data;
}

/**
 * Direct PUT to S3 with XHR so we can report upload progress.
 * Returns when the upload completes; throws on network/HTTP errors.
 */
export function putToS3WithProgress(
  url: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress(e.loaded / e.total);
      }
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 PUT failed: ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Network error")));
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));
    xhr.send(file);
  });
}
